import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { RootLayout } from '@/components/layout/RootLayout'
import { GlassCard } from '@/components/ui/GlassCard'
import { GlassButton } from '@/components/ui/GlassButton'
import { GlassInput } from '@/components/ui/GlassInput'
import { api } from '@/lib/api'
import { channelProfilesQueryOptions, opsSecretsQueryOptions } from '@/lib/queryOptions'
import { useT } from '@/i18n/provider'

export const Route = createFileRoute('/settings/ops/channels')({
  loader: async ({ context: { queryClient } }) => {
    await Promise.all([
      queryClient.ensureQueryData(channelProfilesQueryOptions()),
      queryClient.ensureQueryData(opsSecretsQueryOptions()),
    ])
  },
  component: OpsChannelsPage,
})

function OpsChannelsPage() {
  const t = useT()
  const qc = useQueryClient()
  const { data: profiles = [] } = useQuery(channelProfilesQueryOptions())
  const { data: secrets = [] } = useQuery(opsSecretsQueryOptions())
  const [name, setName] = useState('')
  const [channelType, setChannelType] = useState<'TELEGRAM' | 'WHATSAPP' | 'PHONE'>('TELEGRAM')
  const [credentialSecretId, setCredentialSecretId] = useState('')
  const [configJson, setConfigJson] = useState('{}')
  const [error, setError] = useState('')

  const createMutation = useMutation({
    mutationFn: api.ops.channelProfiles.create,
    onSuccess: () => {
      setName('')
      setChannelType('TELEGRAM')
      setCredentialSecretId('')
      setConfigJson('{}')
      setError('')
      qc.invalidateQueries({ queryKey: ['ops', 'channel-profiles'] })
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed'),
  })

  return (
    <RootLayout>
      <div className="space-y-6 max-w-3xl">
        <div>
          <h1 className="text-xl font-bold text-white">{t('settings.opsChannels.title')}</h1>
          <p className="text-sm text-white/40 mt-0.5">{t('settings.opsChannels.subtitle')}</p>
        </div>

        <GlassCard className="space-y-4">
          <h2 className="font-semibold text-white text-sm">{t('settings.opsChannels.createTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <GlassInput
              label={t('settings.opsChannels.nameLabel')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('settings.opsChannels.namePlaceholder')}
            />
            <div>
              <label className="text-sm font-medium text-white/70 block mb-1.5">{t('settings.opsChannels.typeLabel')}</label>
              <select
                value={channelType}
                onChange={(e) => setChannelType(e.target.value as 'TELEGRAM' | 'WHATSAPP' | 'PHONE')}
                className="glass w-full px-4 py-2.5 text-white text-sm bg-transparent focus:outline-none focus:ring-2 focus:ring-[var(--oc-accent)]/60"
              >
                <option value="TELEGRAM" style={{ background: '#1a1a2e' }}>Telegram</option>
                <option value="WHATSAPP" style={{ background: '#1a1a2e' }}>WhatsApp</option>
                <option value="PHONE" style={{ background: '#1a1a2e' }}>Phone (Twilio)</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-white/70 block mb-1.5">{t('settings.opsChannels.secretLabel')}</label>
              <select
                value={credentialSecretId}
                onChange={(e) => setCredentialSecretId(e.target.value)}
                className="glass w-full px-4 py-2.5 text-white text-sm bg-transparent focus:outline-none focus:ring-2 focus:ring-[var(--oc-accent)]/60"
              >
                <option value="">{t('settings.opsChannels.secretPlaceholder')}</option>
                {secrets.map((secret) => (
                  <option key={secret.id} value={secret.id} style={{ background: '#1a1a2e' }}>
                    {secret.name} ({secret.provider})
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-white/70 block mb-1.5">{t('settings.opsChannels.configLabel')}</label>
              <textarea
                value={configJson}
                onChange={(e) => setConfigJson(e.target.value)}
                rows={4}
                className="glass w-full px-4 py-2.5 text-white text-sm bg-transparent focus:outline-none focus:ring-2 focus:ring-[var(--oc-accent)]/60"
              />
            </div>
          </div>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <GlassButton
            onClick={() => {
              try {
                const config = JSON.parse(configJson || '{}') as Record<string, unknown>
                createMutation.mutate({
                  name,
                  channelType,
                  credentialSecretId: credentialSecretId || undefined,
                  config,
                })
              } catch {
                setError(t('settings.opsChannels.invalidJson'))
              }
            }}
            loading={createMutation.isPending}
            disabled={!name}
          >
            {t('settings.opsChannels.createButton')}
          </GlassButton>
        </GlassCard>

        <div className="space-y-3">
          {profiles.map((profile) => (
            <GlassCard key={profile.id} size="sm" className="space-y-1">
              <p className="text-sm font-medium text-white">{profile.name}</p>
              <p className="text-xs text-white/40">
                {profile.channelType} · {profile.isActive ? t('common.yes') : t('common.no')} · secret: {profile.credentialSecret?.name ?? t('common.none')}
              </p>
            </GlassCard>
          ))}
        </div>
      </div>
    </RootLayout>
  )
}
